import {
  fetchApi,
  getNetworkInfo,
  getTransifexLocale,
  trackError,
} from '@vend/utilities'
import { tx as heliosTxInstance } from '@lightspeed/design-system-react'
import { REACT_UTILITIES_TRANSIFEX_TAG } from '@vend/react-utilities'
import { BUSINESS_COMPONENTS_TRANSLATION_TAG } from '@vend/business-components'
import { createNativeInstance, tx } from '@transifex/native'
import { ApiReceiptTemplateType } from '@vend/types'
import { receiptSettingsStore } from '../factories/database/receipt-settings-store'
import { receiptTemplateTypesStore } from '../factories/database/receipt-template-types-store'

const txManagerModulePromise = window.System.import('@vend-spa/tx-manager')
let fetchTranslationsPromise: Promise<any>

const TRANSIFEX_LOADING_TIMEOUT = 10000
const WEB_REGISTER_TX_TAG = 'webregister'
const TRANSIFEX_APP_TOKEN = '1/af8dd5b9fec79f9ea0fb7f7dab4842261ff5d5ec'

let defaultTXInstance: typeof tx
let frTXInstance: typeof tx | null = null

// Expose to module so we can use it in the receipt print directive
export let hasPrefixedReceiptTemplateTypes = false
export const FRENCH_CANADIAN_LOCALE = 'fr-CA'
export const RECEIPT_TEMPLATE_SUFFIX = `__${FRENCH_CANADIAN_LOCALE}`

txManagerModulePromise.then(({ getTxInstance }: any) => {
  defaultTXInstance = getTxInstance(WEB_REGISTER_TX_TAG)
})

/**
 * Fetches translations for React based components.
 *
 * @param disableTranslations - Whether to disable translations
 * @returns {Promise<void>}
 */
export async function fetchTranslations(disableTranslations: boolean) {
  if (fetchTranslationsPromise) {
    return fetchTranslationsPromise
  }

  if (disableTranslations) {
    return Promise.resolve()
  }

  fetchTranslationsPromise = txManagerModulePromise
    .then(({ setCurrentLocale, getTxInstance, addTxInstance }: any) => {
      const tx = getTxInstance(WEB_REGISTER_TX_TAG)

      const reactUtilitiesTxInstance = getTxInstance(
        REACT_UTILITIES_TRANSIFEX_TAG
      )
      const businessComponentsTxInstance = getTxInstance(
        BUSINESS_COMPONENTS_TRANSLATION_TAG
      )

      // Adds Helios instance to the tx instance cache, ensuring common config is applied (inc. custom CDS)
      addTxInstance(
        heliosTxInstance,
        'helios-react',
        heliosTxInstance.token,
        `helios-react-${WEB_REGISTER_TX_TAG}`
      )

      tx.controllerOf(reactUtilitiesTxInstance)
      tx.controllerOf(heliosTxInstance)
      tx.controllerOf(businessComponentsTxInstance)

      const locale = getTransifexLocale()

      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          reject(
            new Error(`Timed out attempting to load translations for ${locale}`)
          )
        }, TRANSIFEX_LOADING_TIMEOUT)
      })

      const txCurrentLocalePromise = setCurrentLocale(locale).catch(() => {
        throw new Error(`Can't fetch translations for ${locale}`)
      })

      return Promise.race([txCurrentLocalePromise, timeoutPromise])
    })
    .catch((e: Error) => {
      trackError(e.message, { networkInfo: getNetworkInfo() })
    })

  return fetchTranslationsPromise
}

/**
 * Determines French Canadian translations / template types requirements for receipts.
 */
export const determineFrenchCanadianSettingsForReceipts = () => {
  Promise.all([
    receiptRequiresFrenchCanadian(),
    hasFrenchCanadianTemplateTypes(),
  ]).then(
    async ([receiptRequiresFrenchCanadian, hasFrenchCanadianTemplateTypes]) => {
      if (receiptRequiresFrenchCanadian) {
        // Fetch Transifex Translations
        retrieveFrenchCanadianTranslations()

        if (!hasFrenchCanadianTemplateTypes) {
          // Fetch French Canadian Template Types and add them to the store; for when
          // Web Register is initially synced in non French Canadian lang
          const templateTypes = await retrieveFrenchCanadianTemplateTypes()
          addReceiptTemplateTypesToStore(templateTypes)
        }
      }
    }
  )
}

/**
 * Determines if any receipt setting requires French Canadian translations.
 *
 * @returns {Promise<boolean>}
 */
const receiptRequiresFrenchCanadian = async () => {
  try {
    const receipts = await receiptSettingsStore.all()
    return receipts.some(
      (receipt: any) => receipt.locale === FRENCH_CANADIAN_LOCALE
    )
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Determines if the French Canadian receipt template types are required.
 *
 * @returns {Promise<boolean>}
 */
const hasFrenchCanadianTemplateTypes = async () => {
  try {
    const templates = await receiptTemplateTypesStore.all()

    hasPrefixedReceiptTemplateTypes = templates.some((template: any) =>
      template.name.includes(RECEIPT_TEMPLATE_SUFFIX)
    )

    const hasFrenchCanadianTemplateTypes = templates.some(
      (template: any) => template.locale === FRENCH_CANADIAN_LOCALE
    )

    // If we have either a French Canadian template or a template with the manually added suffix, there is no need to fetch
    // additional templates / add them to the store (we already have them)
    return hasPrefixedReceiptTemplateTypes || hasFrenchCanadianTemplateTypes
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Fetches French Canadian translations from Transifex.
 */
const retrieveFrenchCanadianTranslations = () => {
  if (frTXInstance) {
    return
  }

  frTXInstance = createNativeInstance({
    token: TRANSIFEX_APP_TOKEN,
    filterTags: WEB_REGISTER_TX_TAG,
  })

  frTXInstance.setCurrentLocale('fr_CA')
}

/**
 * Retrieves receipt template types from the API, ensuring that they are returned in French Canadian.
 *
 * @returns {Promise<Array<ApiReceiptTemplateType>>}
 */
const retrieveFrenchCanadianTemplateTypes = async function () {
  try {
    const response = await fetchApi('/api/2.0/receipt_template_types', {
      method: 'GET',
      headers: {
        'Accept-Language': FRENCH_CANADIAN_LOCALE,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to retrieve French Canadian template types')
    }

    const { data } = await response.json()
    return data
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Adds the provided receipt template types to the ReceiptTemplateTypes store,
 * prefixing the name with the French Canadian locale.
 *
 * @param templateTypes
 */
const addReceiptTemplateTypesToStore = async (
  templateTypes: ApiReceiptTemplateType[]
) => {
  try {
    for (const template of templateTypes) {
      await receiptTemplateTypesStore.put({
        ...template,
        name: `${template.name}${RECEIPT_TEMPLATE_SUFFIX}`,
      })
    }

    // We now have prefixed receipt template types in store
    hasPrefixedReceiptTemplateTypes = true
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Returns the appropriate translate function for the provided receipt locale.
 *
 * @param {string} receiptSettingLocale - The locale to get the translate function for
 * @returns {Function} The translate function for the given locale
 */
export const getTForReceiptLocale = (receiptSettingLocale?: string) => {
  if (receiptSettingLocale === FRENCH_CANADIAN_LOCALE && frTXInstance) {
    return frTXInstance.t
  }

  return defaultTXInstance.t
}
